// REVOPS-1 (2026-06-04) — Claim → Financials → Billing Readiness.
// Lead-Adjuster surface: what's billed, what's unbilled, billing instructions,
// and "Submit For Invoice Prep". Admin fee / split / AR / QBO are NEVER shown
// here (server strips them for non-financial roles). Lead Adjusters submit;
// they do not generate invoices.
import { useState, useEffect, useCallback } from "react";
import { apiFetch, fmt } from "../api";

function Money({ v, bold }) {
  return <span style={{ fontFamily: "JetBrains Mono,monospace", fontWeight: bold ? 700 : 400 }}>{fmt.money(v)}</span>;
}

export default function BillingReadiness({ claim, claimId, onWorkChanged }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editInstr, setEditInstr] = useState(false);
  const [instr, setInstr] = useState("");
  const [asOf, setAsOf] = useState("");   // #6A as-of cutoff (blank = all unbilled)

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/claims/${claimId}/billing-readiness${asOf ? `?as_of=${asOf}` : ""}`)
      .then(r => { setD(r); setInstr(r.billing_instructions || ""); setErr(null); })
      .catch(e => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [claimId, asOf]);
  useEffect(() => { load(); }, [load]);

  const saveInstr = () => {
    setBusy(true);
    apiFetch(`/claims/${claimId}/billing-instructions`, { method: "PATCH", body: { billing_instructions: instr } })
      .then(() => { setEditInstr(false); load(); onWorkChanged && onWorkChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };
  const submit = () => {
    if (!window.confirm(asOf
        ? `Submit unbilled time & expenses dated on/before ${asOf} for invoice prep? They'll be locked from further billing until prepared.`
        : "Submit all unbilled time & expenses for invoice prep? They'll be locked from further billing until prepared.")) return;
    setBusy(true);
    apiFetch(`/claims/${claimId}/submit-invoice-prep`, { method: "POST", body: { as_of_date: asOf || null } })
      .then(r => { window.alert(`Submitted for prep — ${r.time_entries} time + ${r.expense_entries} expense entr(ies), ${fmt.money((r.service_fee_total || 0) + (r.expense_total || 0))} total.`); load(); onWorkChanged && onWorkChanged(); })
      .catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };
  const reclaimDraft = (id) => {
    if (!window.confirm("Reclaim this Draft invoice? Its time & expenses return to unbilled so you can re-submit. (Draft only — not issued.)")) return;
    setBusy(true);
    apiFetch(`/fee-invoices/${id}/reclaim-draft`, { method: "POST", body: {} })
      .then(() => { load(); onWorkChanged && onWorkChanged(); }).catch(e => setErr(String(e.message || e))).finally(() => setBusy(false));
  };

  if (loading) return <div className="empty-state" style={{ padding: 24 }}>Loading billing readiness…</div>;
  if (!d) return <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{err}</div></div>;

  const s = d.summary || {};
  const th = { padding: "6px 10px", textAlign: "left", fontSize: 11, color: "var(--text3)", textTransform: "uppercase" };
  const td = { padding: "6px 10px", fontSize: 13 };
  const hasUnbilled = (d.unbilled_time || []).length + (d.unbilled_expenses || []).length > 0;
  return (
    <div>
      {err && <div className="card" style={{ marginBottom: 12 }}><div className="card-body" style={{ color: "var(--red)", fontSize: 13 }}>{err}</div></div>}

      {/* Billing instructions — prominent */}
      <div className="card" style={{ marginBottom: 14, borderColor: "var(--navy)" }}>
        <div className="section-header"><span className="section-title">Billing Instructions</span>
          {d.can_submit_prep && !editInstr && <button className="btn btn-outline btn-sm" onClick={() => setEditInstr(true)}>Edit</button>}</div>
        <div className="card-body">
          {editInstr ? (
            <div>
              <textarea style={{ width: "100%", minHeight: 70, padding: 8, fontSize: 13, border: "1px solid var(--border)", borderRadius: 6 }}
                value={instr} onChange={e => setInstr(e.target.value)} placeholder="e.g. Bill lead underwriter only; net 30; PO# required…" />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => { setEditInstr(false); setInstr(d.billing_instructions || ""); }} disabled={busy}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={saveInstr} disabled={busy}>Save</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: d.billing_instructions ? "var(--text1)" : "var(--text3)", whiteSpace: "pre-wrap" }}>
              {d.billing_instructions || "No billing instructions set for this claim."}
            </div>
          )}
        </div>
      </div>

      {/* Billing summary */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "center", padding: "14px 20px" }}>
          <Metric label="Unbilled service fees" value={fmt.money(s.unbilled_service_fees || 0)} />
          <Metric label="Unbilled expenses" value={fmt.money(s.unbilled_expenses || 0)} />
          <Metric label="Potential billing value" value={fmt.money(s.potential_billing_value || 0)} accent />
          <div style={{ flex: 1 }} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <label style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>Invoice through (as-of)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
              <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
                style={{ padding: "4px 7px", border: "1px solid var(--border)", borderRadius: 5, fontSize: 13 }}
                title="Only include unbilled entries dated on or before this date (e.g. invoice July in August). Blank = all unbilled." />
              {asOf && <button className="btn btn-outline btn-sm" style={{ padding: "2px 8px" }} onClick={() => setAsOf("")}>All</button>}
            </div>
          </div>
          {d.can_submit_prep && (
            <button className="btn btn-primary" onClick={submit} disabled={busy || !hasUnbilled}
              title={hasUnbilled ? "Submit unbilled work for invoice prep" : "Nothing unbilled to submit"}>Submit For Invoice Prep</button>
          )}
        </div>
        {(d.receipt_holds || []).length > 0 && (
          <div className="card-body" style={{ paddingTop: 0, fontSize: 12, color: "#991b1b" }}>
            ⚠ {d.receipt_holds.length} expense(s) ≥ $75 missing a receipt — these block invoicing until a receipt is uploaded or a manager approves.
          </div>
        )}
      </div>

      {/* Current unbilled */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="section-header"><span className="section-title">Current Unbilled Work</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", padding: "8px 12px" }}>Time ({(d.unbilled_time || []).length})</div>
          {(d.unbilled_time || []).length === 0 ? <div className="empty-state" style={{ padding: 12, fontSize: 12 }}>No unbilled time.</div> : (
            <table style={{ width: "100%" }}><thead><tr><th style={th}>Date</th><th style={th}>Hours</th><th style={th}>Description</th><th style={{ ...th, textAlign: "right" }}>Amount</th></tr></thead>
              <tbody>{d.unbilled_time.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{fmt.date(r.work_date)}</td><td style={td}>{r.hours}</td>
                  <td style={{ ...td, color: "var(--text3)", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.description || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><Money v={r.amount} /></td></tr>))}</tbody></table>
          )}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", padding: "8px 12px", borderTop: "1px solid var(--border)" }}>Expenses ({(d.unbilled_expenses || []).length})</div>
          {(d.unbilled_expenses || []).length === 0 ? <div className="empty-state" style={{ padding: 12, fontSize: 12 }}>No unbilled expenses.</div> : (
            <table style={{ width: "100%" }}><thead><tr><th style={th}>Date</th><th style={th}>Type</th><th style={th}>Description</th><th style={{ ...th, textAlign: "right" }}>Amount</th></tr></thead>
              <tbody>{d.unbilled_expenses.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{fmt.date(r.expense_date)}</td><td style={td}>{r.expense_type || "—"}{r.receipt_missing && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#fee2e2", color: "#991b1b" }}>NO RECEIPT</span>}</td>
                  <td style={{ ...td, color: "var(--text3)" }}>{r.description || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><Money v={r.amount} /></td></tr>))}</tbody></table>
          )}
        </div>
      </div>

      {/* Previously invoiced */}
      <div className="card">
        <div className="section-header"><span className="section-title">Previously Invoiced ({(d.previously_invoiced || []).length})</span></div>
        <div className="card-body" style={{ padding: 0 }}>
          {(d.previously_invoiced || []).length === 0 ? <div className="empty-state" style={{ padding: 16, fontSize: 12 }}>No invoices yet for this claim.</div> : (
            <table style={{ width: "100%", fontSize: 13 }}>
              <thead><tr><th style={th}>Invoice #</th><th style={th}>Date</th><th style={th}>Type</th><th style={{ ...th, textAlign: "right" }}>Service</th><th style={{ ...th, textAlign: "right" }}>Expense</th><th style={{ ...th, textAlign: "right" }}>Total</th><th style={th}>Status</th></tr></thead>
              <tbody>{d.previously_invoiced.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{r.invoice_number || "—"}{r.is_historical && <span className="badge" style={{ marginLeft: 6, fontSize: 9, background: "#ede9fe", color: "#6d28d9" }}>VCA</span>}</td>
                  <td style={td}>{fmt.date(r.invoice_date)}</td><td style={td}>{r.invoice_type || "—"}</td>
                  <td style={{ ...td, textAlign: "right" }}><Money v={r.service_fee_total} /></td>
                  <td style={{ ...td, textAlign: "right" }}><Money v={r.expense_total} /></td>
                  <td style={{ ...td, textAlign: "right" }}><Money v={r.invoice_total} bold /></td>
                  <td style={td}><span className="badge" style={{ fontSize: 10, background: r.status === "Paid" ? "#dcfce7" : "#dbeafe", color: r.status === "Paid" ? "#166534" : "#1e40af" }}>{r.status}</span>{r.status === "Draft" && d.can_submit_prep && <button className="btn btn-outline btn-sm" style={{ marginLeft: 8, padding: "1px 7px", fontSize: 10 }} disabled={busy} onClick={() => reclaimDraft(r.id)}>Reclaim</button>}</td></tr>))}</tbody></table>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div style={{ minWidth: 120 }}>
      <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "JetBrains Mono,monospace", marginTop: 1, color: accent ? "var(--navy)" : "inherit" }}>{value}</div>
    </div>
  );
}
